import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { animate, stagger } from "animejs";
import { shouldReduceMotion } from "../../utils/motion";
import { FaEnvelope, FaLinkedin, FaGithub } from "react-icons/fa";
import "./Contact.scss";

const formVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } }
};

const fieldVariant = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1, y: 0,
    transition: { type: 'spring', stiffness: 200, damping: 22 }
  }
};

const socialLinks = [
  { icon: <FaLinkedin />, label: 'LinkedIn', href: 'https://www.linkedin.com/in/ahmad-bin-khalil', sub: '/in/ahmad-bin-khalil' },
  { icon: <FaGithub />,   label: 'GitHub',   href: 'https://github.com/AhmadBinKhalil', sub: '/AhmadBinKhalil' },
  { icon: <FaEnvelope />, label: 'Email',    href: 'mailto:ahmadkhalil2142@gmail.com', sub: 'ahmadkhalil2142@gmail.com' },
];

const Contact = () => {
  const form = useRef();
  const rootRef = useRef(null);
  const [status, setStatus] = useState({ message: "", type: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!rootRef.current || shouldReduceMotion()) return undefined;

    const animation = animate(rootRef.current.querySelectorAll(".contact__info, .contact__form-panel, .contact__footer"), {
      opacity: [0, 1],
      translateY: [24, 0],
      delay: stagger(90),
      duration: 720,
      ease: "outExpo"
    });

    return () => animation.revert();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ message: "", type: "" });

    try {
      const formData = new FormData(form.current);
      const response = await fetch("https://n8n-main.apps.coolify.ahmadrajput.com/webhook/website/contact-us", {
        method: "POST",
        body: formData
      });

      if (response.ok) {
        setStatus({ message: "✓ Message sent. I will respond within 24h.", type: "success" });
        form.current.reset();
      } else {
        throw new Error('Failed');
      }
    } catch {
      setStatus({ message: "Failed to send. Please try email directly.", type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact" ref={rootRef}>
      <motion.div
        className="contact__content"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.6 }}
      >
        <motion.h2
          className="contact__heading"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ type: 'spring', stiffness: 200, damping: 22 }}
        >
          Get In Touch
        </motion.h2>

        <motion.p
          className="contact__subtitle"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          Open to automation projects, AI integrations, and consulting engagements.
        </motion.p>

        <div className="contact__grid">
          {/* Info panel */}
          <motion.div
            className="contact__info"
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ type: 'spring', stiffness: 200, damping: 24, delay: 0.1 }}
          >
            <h3>Let us Build</h3>
            <p>
              Whether you need a RAG pipeline, multi-agent automation, or end-to-end
              system integration — let us talk about what's possible.
            </p>

            <div className="contact__social">
              {socialLinks.map(({ icon, label, href, sub }) => (
                <motion.a
                  key={label}
                  href={href}
                  target={href.startsWith("mailto") ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.97 }}
                >
                  {icon}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem', color: "#243044" }}>{label}</div>
                    <div style={{ fontSize: '0.78rem', opacity: 0.55 }}>{sub}</div>
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Form panel */}
          <motion.div
            className="contact__form-panel"
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ type: 'spring', stiffness: 200, damping: 24, delay: 0.15 }}
          >
            <motion.form
              ref={form}
              name="contact"
              aria-label="Contact form"
              aria-describedby="contact-form-help"
              onSubmit={handleSubmit}
              variants={formVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <p id="contact-form-help" className="sr-only">
                Send Ahmad Rajput a message about an automation, AI integration, or consulting project.
              </p>
              <motion.div className="form__group" variants={fieldVariant}>
                <label htmlFor="user_name">Name</label>
                <input id="user_name" type="text" name="user_name" placeholder="Your name" autoComplete="name" required />
              </motion.div>

              <motion.div className="form__group" variants={fieldVariant}>
                <label htmlFor="user_email">Email</label>
                <input id="user_email" type="email" name="user_email" placeholder="your@email.com" autoComplete="email" required />
              </motion.div>

              <motion.div className="form__group" variants={fieldVariant}>
                <label htmlFor="message">Message</label>
                <textarea id="message" name="message" placeholder="Tell me about your project..." autoComplete="off" required />
              </motion.div>

              <motion.div variants={fieldVariant}>
                <motion.button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                  whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                  whileTap={!isSubmitting ? { scale: 0.97 } : {}}
                  transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                >
                  {isSubmitting ? 'Sending…' : 'Send Message'}
                </motion.button>
              </motion.div>

              {status.message && (
                <motion.div
                  className={`form__status ${status.type}`}
                  role={status.type === "error" ? "alert" : "status"}
                  aria-live={status.type === "error" ? "assertive" : "polite"}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                >
                  {status.message}
                </motion.div>
              )}
            </motion.form>
          </motion.div>
        </div>

        <div className="contact__footer">
          Built with React · Designed with precision · © 2026 Ahmad Rajput
        </div>
      </motion.div>
    </section>
  );
};

export default Contact;
